import { useQuery } from "@tanstack/react-query";

import type { Invoice } from "~/shared/schemas/invoice-schema.ts";
import { invoiceArraySchema } from "~/shared/schemas/invoice-schema.ts";
import { apiRoutes } from "~/shared/services/api/api-routes.ts";
import { getAndValidate } from "~/shared/services/api/fetch-client.ts";
import type { InvoiceFilter } from "~/shared/types/payment-types";

import type { InvoiceStatus } from "./use-invoice-selection";

const filterToRouteMap: Record<InvoiceStatus, string> = {
	openInvoices: apiRoutes.GETCUSTOMEROPENINVOICES,
	pendingPayment: apiRoutes.GETCUSTOMEROPENINVOICES,
	paidInvoices: apiRoutes.GETCUSTOMERPAIDINVOICES,
	allInvoices: apiRoutes.GETCUSTOMERALLINVOICES,
};

export const fetchInvoices = async (
	status: InvoiceStatus,
	signal: AbortSignal,
	filter: InvoiceFilter | null,
) => {
	const baseRoute = filterToRouteMap[status];
	const isPendingPayment = status === "pendingPayment";
	const searchParams = new URLSearchParams();
	if (filter) {
		searchParams.append(`${filter}`, "true");
	}
	if (isPendingPayment) {
		searchParams.append("pendingPayments", "true");
	}

	const params = searchParams.toString();
	const queryString = params.length > 0 ? `?${params}` : "";
	const urlWithQuery = `${baseRoute}${queryString}`;
	try {
		const resp = await getAndValidate(urlWithQuery, invoiceArraySchema, {
			signal,
		});

		if (resp.success) {
			const now = new Date();
			return (
				(resp.data.map((item) => {
					const isPastDue =
						!!item.dueDate &&
						new Date(item.dueDate) < now &&
						item.balanceAmt > 0 &&
						!item.invoiceStatus?.toLowerCase().includes("payment pending");
					return {
						id: item.invoiceID,
						...item,
						invoiceStatus: isPastDue ? "past-due" : item.invoiceStatus,
					};
				}) as unknown as Invoice[]) ?? []
			);
		}

		console.error(`Failed to fetch ${status} invoices:`, resp.error);
		return [];
	} catch (error) {
		console.error(`Failed to fetch ${status} invoices:`, error);
		return [];
	}
};

export const useFetchInvoices = (
	invoiceStatus: InvoiceStatus = "openInvoices",
	invoiceFilter: InvoiceFilter | null = null,
) => {
	const { data, isLoading, isFetching, error } = useQuery({
		queryKey: ["customerInvoices", invoiceStatus, invoiceFilter],
		queryFn: ({ signal }) =>
			fetchInvoices(invoiceStatus, signal, invoiceFilter),
		staleTime: 0,
		gcTime: 0,
	});

	return {
		invoices: data?.sort((a, b) => (b?.age ?? 0) - (a?.age ?? 0)) ?? [],
		isLoading,
		isFetching,
		error,
	};
};
